// @ts-nocheck
/* eslint-disable */
import {
  ContextConsumer,
  createContextProvider,
  Contextualizer,
  LightningElement,
  wire,
  WireAdapter,
} from "lwc";

import { EffectAdapter } from "common/effectAdapter";

export interface ContextWireAdapter<Value, Config, Context>
  extends WireAdapter<Value, Config, Context> {
  value: Value;
}
export interface ContextWireAdapterConstructor<Value, Config, Context> {
  new (setValue: (value: Value) => void): ContextWireAdapter<
    Value,
    Config,
    Context
  >;
  setGlobalContext(context: Context): void;
  setDefaultValue(value: Value): void;
}

export function createContextAdapter<Value, Config, Context>(
  initDefaultValue: () => Value,
  schema: Record<keyof Context, string>
): ContextWireAdapterConstructor<Value, Config, Context> {
  const adapters = new Set<ContextWireAdapter<Value, Config, Context>>();

  let globalContext: Context;
  let defaultValue: Value;

  return class ContextAdapter
    implements ContextWireAdapter<Value, Config, Context> {
    static contextSchema = schema;

    static setGlobalContext(context: Context) {
      globalContext = context;
      (adapters as Set<ContextAdapter>).forEach((adapter) =>
        adapter.setValue(adapter.value)
      );
    }

    static setDefaultValue(value: Value) {
      defaultValue = value;
    }

    constructor(public setValue: (value: Value) => void) {
      if (!defaultValue) {
        defaultValue = initDefaultValue();
      }
    }

    private config?: Config;
    private context?: Context;

    get value(): Value {
      return {
        ...defaultValue,
        ...compactConfig<Context>(globalContext),
        ...compactConfig<Context>(this.context),
        ...compactConfig<Config>(this.config),
      };
    }

    connect() {
      adapters.add(this);
      this.setValue(this.value);
    }

    disconnect() {
      adapters.delete(this);
    }

    update(config: Config, context?: Context) {
      this.config = config;
      this.context = context;
      this.setValue(this.value);
    }
  };
}

function compactConfig<T>(config?: T) {
  return Object.fromEntries(
    Object.entries({ ...config }).filter(
      (keyValue) => typeof keyValue[1] !== "undefined" && keyValue[1] !== null
    )
  );
}

export function createBaseContextProviderElement<Value, Config, Context>(
  adapterClass: ContextWireAdapterConstructor<Value, Config, Context>
) {
  const contextualizer = createContextProvider(adapterClass);

  return class extends BaseProvider<Context> {
    public get contextualizer() {
      return contextualizer;
    }
  };
}

class BaseProvider<Context> extends LightningElement {
  consumers = new Set<ContextConsumer<Context>>();

  public get contextualizer(): Contextualizer<Context> {
    throw new Error("contextualizer not implenented");
  }

  @wire(EffectAdapter, {
    localContext: "$localContext",
  })
  updateConsumers({ localContext }: { localContext: Context }) {
    for (let consumer of this.consumers) {
      consumer.provide(localContext);
    }
  }

  public get localContext(): Context {
    return {} as Context;
  }

  connectedCallback() {
    this.contextualizer(this, {
      consumerConnectedCallback: (consumer) => {
        this.consumers.add(consumer);
        consumer.provide(this.localContext);
      },
      consumerDisconnectedCallback: (consumer) => {
        this.consumers.delete(consumer);
      },
    });
  }
}
